import { useEffect, useRef, useState } from "react";
import { aiRequest } from "../../apis/ai";

export function useVoiceCall({ threadId, mode, onThread, onTurns, onError }) {
  const [state, setState] = useState("idle");
  const [muted, setMuted] = useState(false);
  const live = useRef(null);
  const generation = useRef(0);
  const handlers = useRef({ threadId, mode, onThread, onTurns, onError });
  handlers.current = { threadId, mode, onThread, onTurns, onError };
  const mounted = useRef(true);

  const cleanup = (notify = true) => {
    generation.current += 1;
    const session = live.current;
    live.current = null;
    if (session) {
      clearInterval(session.poll);
      clearTimeout(session.expiry);
      session.stream?.getTracks().forEach((track) => track.stop());
      session.peer?.close();
      if (session.audio) {
        session.audio.srcObject = null;
        session.audio.remove();
      }
      if (session.id) aiRequest(`/voice/${encodeURIComponent(session.id)}`, { method: "DELETE" }).catch(() => {});
    }
    if (notify && mounted.current) { setState("idle"); setMuted(false); }
  };

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; cleanup(false); };
  }, []);

  const start = async () => {
    if (live.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === "undefined") {
      handlers.current.onError("Live voice requires a supported browser on HTTPS or localhost.");
      return;
    }
    const current = ++generation.current;
    const session = {};
    live.current = session;
    setState("connecting");
    try {
      session.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      if (generation.current !== current) { session.stream.getTracks().forEach((track) => track.stop()); return; }
      session.peer = new RTCPeerConnection({ bundlePolicy: "max-bundle", iceServers: [] });
      session.audio = document.createElement("audio");
      session.audio.autoplay = true;
      session.audio.hidden = true;
      document.body.appendChild(session.audio);
      session.peer.ontrack = (event) => {
        session.audio.srcObject = event.streams[0];
        session.audio.play().catch(() => handlers.current.onError("Audio playback was blocked. End the call and start it again from the voice button."));
      };
      session.stream.getTracks().forEach((track) => session.peer.addTrack(track, session.stream));
      session.channel = session.peer.createDataChannel("erp-voice");
      session.channel.onopen = () => { if (mounted.current && generation.current === current) setState("live"); };
      session.peer.onconnectionstatechange = () => {
        if (["failed", "disconnected"].includes(session.peer.connectionState) && live.current === session) {
          handlers.current.onError("Voice connection interrupted. Start another call to reconnect.");
          cleanup();
        }
      };
      const offer = await session.peer.createOffer();
      await session.peer.setLocalDescription(offer);
      const response = await aiRequest("/voice", { method: "POST", body: JSON.stringify({ offer_sdp: session.peer.localDescription.sdp, thread_id: handlers.current.threadId || undefined, mode: handlers.current.mode }) });
      session.id = response.session_id;
      if (generation.current !== current) { aiRequest(`/voice/${encodeURIComponent(session.id)}`, { method: "DELETE" }).catch(() => {}); return; }
      session.threadId = response.thread_id;
      handlers.current.onThread(response.thread_id);
      await session.peer.setRemoteDescription({ type: "answer", sdp: response.answer_sdp });
      let refreshing = false;
      session.poll = setInterval(async () => {
        if (refreshing || live.current !== session) return;
        refreshing = true;
        try {
          const history = await aiRequest(`/threads/${encodeURIComponent(session.threadId)}`);
          if (live.current === session && mounted.current) handlers.current.onTurns(history.turns || []);
        } catch (caught) {
          if (live.current === session) { handlers.current.onError(caught.message); cleanup(); }
        } finally { refreshing = false; }
      }, 2000);
      session.expiry = setTimeout(() => cleanup(), Math.max(0, Date.parse(response.expires_at) - Date.now()));
    } catch (caught) {
      if (generation.current === current) {
        handlers.current.onError(caught.name === "NotAllowedError" ? "Microphone permission was denied." : caught.message || "Voice call could not start.");
        cleanup();
      }
    }
  };

  const toggleMute = () => {
    const stream = live.current?.stream;
    if (!stream) return;
    const disabled = !muted;
    stream.getAudioTracks().forEach((track) => { track.enabled = !disabled; });
    setMuted(disabled);
  };

  return { state, active: state !== "idle", muted, start, stop: cleanup, toggleMute };
}